import { getSrc } from 'gatsby-plugin-image';
import { notEmpty } from 'utils/types/utilityTypes';

import {
  HighlightProps,
  HighlightVisualTheme,
  HIGHLIGHT_THEMES,
} from './Highlight';

type PDPSection = Queries.pdpSectionFragment;
type HighlightEntry = Queries.HighlightEntryFragment | null | undefined;

const DEFAULT_THEME_KEY: HighlightVisualTheme = 'moonlight';

const hasEssentialContent = (highlight?: HighlightProps | null): boolean =>
  Boolean(highlight?.headline || highlight?.description);

const mapBackgroundColorToTheme = (
  backgroundColor?: string | null,
): HighlightVisualTheme => {
  if (!backgroundColor) {
    return DEFAULT_THEME_KEY;
  }

  const normalized = backgroundColor.toLowerCase();

  if (normalized.includes('moonlight')) {
    return 'moonlight';
  }

  return 'default';
};

const resolveThemeFromEntry = (
  theme?: string | null,
): HighlightVisualTheme => {
  if (!theme) {
    return DEFAULT_THEME_KEY;
  }

  const normalizedTheme = theme.toLowerCase() as HighlightVisualTheme;

  if (HIGHLIGHT_THEMES[normalizedTheme]) {
    return normalizedTheme;
  }

  return DEFAULT_THEME_KEY;
};

const parseHighlightEntry = (
  entry?: HighlightEntry,
): HighlightProps | null => {
  if (!entry) {
    return null;
  }

  const theme = resolveThemeFromEntry(entry.theme ?? undefined);
  const imageData = entry.image?.gatsbyImageData;
  const imageSrc = imageData
    ? getSrc(imageData) ?? entry.image?.file?.url ?? undefined
    : entry.image?.file?.url ?? undefined;
  const highlight: HighlightProps = {
    headline: entry.headline ?? undefined,
    description: entry.description ?? undefined,
    link: entry.link ?? undefined,
    image: imageSrc ?? undefined,
    imageAlt: entry.image?.description ?? entry.image?.title ?? undefined,
    theme,
    showOnPDP: entry.showOnPdp ?? true,
    backgroundColorToken: entry.backgroundColorToken ?? undefined,
  };

  return hasEssentialContent(highlight) ? highlight : null;
};

export const parseHighlightSection = (
  section?: PDPSection | null,
  entry?: HighlightEntry,
): HighlightProps | null => {
  // If no section provided, return null (caller should handle)
  if (!section && !entry) {
    return null;
  }

  const entryHighlight = parseHighlightEntry(entry);
  if (entryHighlight) {
    return entryHighlight;
  }

  if (!section) {
    return null;
  }

  const metadata = section.metadata;
  const primaryCta = metadata?.ctas?.filter(notEmpty)?.[0];
  const firstImage = section.images?.filter(notEmpty)?.[0];
  const desktopImage = firstImage?.desktopImage?.gatsbyImageData;

  const resolvedTheme = mapBackgroundColorToTheme(
    metadata?.theme?.backgroundColor ?? undefined,
  );

  const imageSrc = desktopImage ? getSrc(desktopImage) ?? undefined : undefined;
  const showOnPdp =
    (section as unknown as Queries.ContentfulHighlight)?.showOnPdp ?? undefined;

  const theme = HIGHLIGHT_THEMES[resolvedTheme]
    ? resolvedTheme
    : DEFAULT_THEME_KEY;


  // Always return valid props with defaults
  const highlight = {
    headline: metadata?.title ?? metadata?.context?.title ?? undefined,
    description: metadata?.description?.description ?? undefined,
    link: primaryCta?.url ?? undefined,
    image: imageSrc ?? undefined,
    imageAlt:
      firstImage?.desktopImageAltText ??
      firstImage?.mobileImageAltText ??
      undefined,
    theme,
    showOnPDP: showOnPdp ?? true,
    backgroundColorToken: metadata?.theme?.backgroundColor ?? undefined,
  } as HighlightProps;

  return hasEssentialContent(highlight) ? highlight : null;
};

export default parseHighlightSection;
